import axios from "axios";

const request = axios.create({
  baseURL: process.env.VUE_APP_URL || "http://localhost:4001/api/v1",
  timeout: 15000, // 15 segundos para operaciones que envían emails
});

let authService = null;
// Importación lazy para evitar dependencias circulares
const getAuthService = () => {
  if (!authService) {
    authService = require("./auth.service").default;
  }
  return authService;
};

// Interceptor de peticiones - agregar token
request.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("accessToken");
    
    if (accessToken) {
      config.headers["Authorization"] = accessToken;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de respuestas - manejo de errores estandarizado
request.interceptors.response.use(
  (response) => {
    // Respuestas exitosas (200-299) se retornan directamente
    return response;
  },
  (error) => {
    console.error("Error en petición:", error);
    
    // Si no hay respuesta del servidor
    if (!error.response) {
      return Promise.reject({
        message: "Error de conexión. Verifique su conexión a internet",
        status: 0
      });
    }
    
    const { status, data } = error.response;
    
    // Manejo específico por código de estado
    switch (status) {
      case 400: // Validación
        return Promise.reject({
          message: data.message || "Datos inválidos",
          errors: data.errors || [],
          status: 400
        });
      
      case 401: { // No autorizado / Token inválido o revocado
        // Verificar si es un token revocado específicamente
        const isRevokedToken = data.message && (
          data.message.includes("revocado") || 
          data.message.includes("Token revocado") ||
          data.message.includes("blacklist")
        );

        if (isRevokedToken) {
          // Token revocado: limpiar sesión y redirigir
          localStorage.removeItem("user");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("company");
          localStorage.removeItem("slug");
          
          // Limpiar cache de auth
          try {
            const auth = getAuthService();
            auth.clearCache();
          } catch (e) {
            // Ignorar error si no se puede cargar authService
          }
          
          // Mostrar mensaje al usuario si está disponible
          if (typeof window !== 'undefined' && window.location) {
            window.location.href = "/";
          }
        }

        return Promise.reject({
          message: data.message || "Credenciales incorrectas o sesión expirada",
          status: 401,
          isRevokedToken
        });
      }
      
      case 403: { // Prohibido
        // Limpiar sesión y redirigir al login
        localStorage.removeItem("user");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("company");
        localStorage.removeItem("slug");
        
        // Limpiar cache de auth
        try {
          const auth = getAuthService();
          auth.clearCache();
        } catch (e) {
          // Ignorar error si no se puede cargar authService
        }
        
        if (typeof window !== 'undefined' && window.location) {
          window.location.href = "/";
        }
        
        return Promise.reject({
          message: data.message || "No tiene permisos para realizar esta acción",
          status: 403
        });
      }
      
      case 404: // No encontrado
        return Promise.reject({
          message: data.message || "Recurso no encontrado",
          status: 404
        });
      
      case 409: // Conflicto (ej: email duplicado)
        return Promise.reject({
          message: data.message || "El recurso ya existe",
          status: 409
        });
      
      case 429: // Rate limit
        return Promise.reject({
          message: data.message || "Demasiadas solicitudes. Intente más tarde",
          status: 429
        });
      
      case 500: // Error del servidor
        return Promise.reject({
          message: data.message || "Error interno del servidor",
          status: 500
        });
      
      default:
        return Promise.reject({
          message: data.message || "Error inesperado",
          status: status
        });
    }
  }
);

export default request;

// Helper para mostrar errores de forma consistente
export function handleApiError(error, defaultMessage = "Ha ocurrido un error") {
  let message = defaultMessage;
  let title = "Error";

  if (error.status === 400) {
    title = "Datos inválidos";
    message = error.message || "Por favor, verifica los datos ingresados";
    
    // Verificar si es un error de XSS
    if (error.message && (
      error.message.toLowerCase().includes("xss") ||
      error.message.toLowerCase().includes("contenido peligroso") ||
      error.message.toLowerCase().includes("patrón peligroso")
    )) {
      title = "Contenido no permitido";
      message = "Tu mensaje contiene caracteres o etiquetas no permitidas. Por favor, revisa tu texto.";
    }
  } else if (error.status === 401) {
    title = "No autorizado";
    if (error.isRevokedToken) {
      title = "Sesión cerrada";
      message = "Tu sesión ha sido cerrada. Por favor, inicia sesión nuevamente.";
    } else {
      message = "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.";
    }
  } else if (error.status === 403) {
    title = "Acceso denegado";
    message = error.message || "No tienes permisos para realizar esta acción";
  } else if (error.status === 404) {
    title = "No encontrado";
    message = error.message || "El recurso solicitado no existe";
  } else if (error.status === 429) {
    title = "Demasiadas solicitudes";
    message = "Por favor, espera un momento antes de intentar nuevamente";
  } else if (error.status >= 500) {
    title = "Error del servidor";
    message = "Estamos experimentando problemas. Intenta más tarde";
  } else if (error.message) {
    message = error.message;
  }

  return { title, message, status: error.status };
}